import type { ReactNode } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import GeographicalFilterPanel from "@/components/GeographicalFilter";
import { useGeographyFilter } from "@/contexts/GeographyFilterContext";

export interface GeographicalFilterState {
  geographyId?: string;
}

interface GeographicalFilterToolbarProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedFilters?: GeographicalFilterState;
  onFilterChange?: (filters: GeographicalFilterState) => void;
  children?: ReactNode;
}

/** Кнопка на карте ОИ: открывает общую панель территории. */
export function GeographicalFilterToolbar({ children }: GeographicalFilterToolbarProps) {
  const { territories, setSheetOpen } = useGeographyFilter();

  return (
    <div className="pointer-events-auto relative z-[100] ml-auto hidden shrink-0 items-center gap-1 p-1 sm:flex sm:p-2">
      <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-xs font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          title="Территории: АТД, округа, контур на карте"
          onClick={() => setSheetOpen(true)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
          <span>Территории</span>
          {territories.length > 0 && (
            <Badge variant="secondary" className="h-5 max-w-24 truncate px-1 text-[10px]">
              {territories.length}
            </Badge>
          )}
        </Button>
        {children}
      </div>
    </div>
  );
}

function clearBrowserTextSelection() {
  window.getSelection()?.removeAllRanges();
}

export function GeographyFilterSheetHost() {
  const { sheetOpen, setSheetOpen } = useGeographyFilter();

  return (
    <Sheet
      open={sheetOpen}
      onOpenChange={(open) => {
        if (!open) clearBrowserTextSelection();
        setSheetOpen(open);
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full select-none flex-col overflow-hidden sm:max-w-md [&_input]:select-text [&_textarea]:select-text"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          clearBrowserTextSelection();
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setSheetOpen(false);
        }}
      >
        <SheetHeader className="shrink-0 space-y-1 text-left">
          <SheetTitle>Географические фильтры</SheetTitle>
        </SheetHeader>
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
          <GeographicalFilterPanel />
        </div>
      </SheetContent>
    </Sheet>
  );
}
